import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

interface Props {
  openPopup?: () => void;
}

/**
 * Mobile-only sticky bottom CTA shown on the landing page for logged-out users.
 * - Primary: Activate FREE Trial (animated)
 * - Secondary: Log In
 */
const MobileStickyCTA: React.FC<Props> = ({ openPopup }) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  // Detect auth (session token used elsewhere in app)
  const isAuthed = useMemo(() => !!sessionStorage.getItem('STOCK_AUTH_TOKEN'), []);

  // Mobile viewport state (<= 640px ~ Tailwind sm)
  const [isMobile, setIsMobile] = useState<boolean>(() => typeof window !== 'undefined' ? window.innerWidth <= 640 : false);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 640);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const onActivate = () => {
    if (openPopup) openPopup();
    else navigate('/signup');
  };
  const onLogin = () => {
    if (openPopup) openPopup();
    else navigate('/login');
  };

  // Only show on landing page, when logged out, on mobile
  const show = !isAuthed && isMobile && pathname === '/';
  const barRef = useRef<HTMLDivElement | null>(null);

  // Reserve space to avoid covering content
  useEffect(() => {
    if (!show) {
      document.body.style.paddingBottom = '';
      return;
    }
    const el = barRef.current;
    if (!el) return;
    const applyPadding = () => {
      const h = el.offsetHeight || 0;
      document.body.style.paddingBottom = `${h}px`;
    };
    applyPadding();
    const ro = new ResizeObserver(applyPadding);
    ro.observe(el);
    window.addEventListener('resize', applyPadding);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', applyPadding);
      document.body.style.paddingBottom = '';
    };
  }, [show]);

  if (!show) return null;

  return (
    <div
      ref={barRef}
      className="fixed inset-x-0 bottom-0 z-40 border-t border-theme-tertiary backdrop-blur bg-theme-secondary/90 px-3 pt-3 pb-3 animate-slideIn"
      style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)' }}
      role="region"
      aria-label="Mobile call to action"
    >
      <div className="max-w-screen-sm mx-auto flex items-center gap-2">
        <button
          onClick={onActivate}
          className="flex-1 rounded-full bg-gradient-to-r from-indigo-600 to-purple-600 text-theme-primary font-semibold py-3 px-4 text-sm shadow-md hover:from-indigo-700 hover:to-purple-700 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-400 animate-pulse"
          aria-label="Activate free trial"
        >
          Activate FREE Trial
        </button>
        <button
          onClick={onLogin}
          className="rounded-full border border-theme-tertiary text-theme-primary font-semibold py-3 px-4 text-sm bg-theme-secondary hover:bg-theme-tertiary transition-colors"
          aria-label="Log in"
        >
          Log In
        </button>
      </div>
    </div>
  );
};

export default MobileStickyCTA;
